import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server, Info } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './IndentDemandReport.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const NearExpiry = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [lifeRangeFrom, setLifeRangeFrom] = useState(1);
  const [lifeRangeTo, setLifeRangeTo] = useState(367);
  const [searchTerm, setSearchTerm] = useState('');
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  const [selectedItemObj, setSelectedItemObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    localStorage.clear();
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading Near Expiry Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we fetch near expiry data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  // NEW: GT/MT Information Banner Component
  const GTMTInfoBanner = () => {
    if (!branchType || (branchType !== 'GT' && branchType !== 'MT')) {
      return null;
    }

    return (
      <div style={{
        backgroundColor: '#e3f2fd',
        border: '2px solid #2196f3',
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '15px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)'
      }}>
        <Info size={24} color="#1976d2" />
        <div>
          <div style={{ 
            color: '#1976d2', 
            fontSize: '16px', 
            fontWeight: '700',
            marginBottom: '4px'
          }}>
            📍 {branchType} Segment Information
          </div>
          <div style={{ 
            color: '#1565c0', 
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Production data is available for plants: <strong>L111</strong> and <strong>L135</strong>
          </div>
          <div style={{ 
            color: '#1565c0', 
            fontSize: '13px',
            marginTop: '2px',
            fontStyle: 'italic'
          }}>
            Select the appropriate area and branch to view near expiry data from these production plants.
          </div>
        </div>
      </div>
    );
  };

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          const allOption = { SFAreaId: 'ALL', SFAreaName: 'ALL', sfAreaId: 'ALL', sfAreaName: 'ALL' };
          setAreas([allOption, ...data]);
          setSelectedAreaObj(null);
          setBranches([]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
        }
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
        }
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // Fetch Items (placeholder - you can implement this if needed)
  const fetchItems = async () => {
    try {
      // This endpoint might need to be adjusted based on your API
      setItems([{ ItemId: 'ALL', ItemName: 'ALL' }]);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([{ ItemId: 'ALL', ItemName: 'ALL' }]);
    }
  };

  // UPDATED: Fetch Near Expiry Data with improved response handling
  const fetchNearExpiryData = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    setLoading(true);
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);

    try {
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
      const itemId = selectedItemObj?.ItemId || 'ALL';
      const batchNumber = selectedBatch || 'ALL';
      const fromDateStr = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
      const toDateStr = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');

      console.log('Fetching data with params:', {
        areaId,
        branchId,
        itemId,
        batchNumber,
        lifeRangeFrom,
        lifeRangeTo,
        fromDateStr,
        toDateStr
      });

      const url = `${local_signage}/api/v1/nearexpiryreport?BizSegmentId=${branchType || 'ALL'}&AreaId=${areaId}&OriginBranchId=${branchId}&ItemID=${itemId}&BatchNumber=${batchNumber}&LifeRangeFrom=${lifeRangeFrom}&LifeRangeTo=${lifeRangeTo}&fromDate=${fromDateStr}&toDate=${toDateStr}`;
      
      const response = await apiCall(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Received data:', data);
        
        // UPDATED: Handle different response formats including empty/no data response
        let transformedData = [];
        
        // Check if response indicates no data
        if (data && Array.isArray(data.Result) && data.Result.length > 0 && data.Result[0].Result === 0) {
          // Handle {"Result":[{"Result":0,"Description":"No Data"}]} format
          console.log('No data response received:', data.Result[0].Description);
          transformedData = [];
        } else if (data && data.BatchBranchMove && Array.isArray(data.BatchBranchMove)) {
          // Handle BatchBranchMove format
          transformedData = data.BatchBranchMove.map((item, index) => {
            const batchDate = dayjs(item.BatchDate);
            const expiryDate = batchDate.add(item.BestForDays, 'day');
            const today = dayjs();
            const daysToExpire = expiryDate.diff(today, 'day');
            
            return {
              id: index + 1,
              sno: index + 1,
              batchNumber: item.BatchNumber,
              prodPlanID: item.ProdPlanID,
              productionPlantID: item.BranchID,
              productionPlantName: item.OriginBranchName || 'N/A',
              salesBranchID: item.SaleDetails && item.SaleDetails.length > 0 ? item.SaleDetails[0].BranchId : 'N/A',
              salesBranchName: item.SaleDetails && item.SaleDetails.length > 0 ? item.SaleDetails[0].BranchName : 'N/A',
              itemId: item.ItemId,
              itemName: item.ItemName,
              batchDate: batchDate.format('DD-MM-YYYY'),
              todaysDate: today.format('DD-MM-YYYY'),
              bestForDays: item.BestForDays,
              prodQty: item.ProdQty || 0,
              totalSaleQty: item.SaleTotals?.TotalSaleQty || 0,
              totalSRQty: item.SaleTotals?.TotalSRQty || 0,
              totalTrInQty: item.SaleTotals?.TotalTrInQty || 0,
              pshopQty: item.PshopQty || 0,
              saleDetails: item.SaleDetails || [],
              expiryDate: expiryDate.format('DD-MM-YYYY'),
              daysToExpire: daysToExpire,
              expiringQty: (item.ProdQty || 0) - (item.SaleTotals?.TotalSaleQty || 0) + (item.SaleTotals?.TotalSRQty || 0) - (item.PshopQty || 0),
              branchID: item.BranchID,
              branchName: item.OriginBranchName || 'N/A',
              storeName: item.OriginBranchName || 'N/A',
              areaName: item.AreaName || 'N/A',
              age: item.Age || 0,
              daysToExpiry: daysToExpire,
              remainingQty: (item.ProdQty || 0) - (item.SaleTotals?.TotalSaleQty || 0) + (item.SaleTotals?.TotalSRQty || 0) - (item.PshopQty || 0)
            };
          });
        } else if (Array.isArray(data)) {
          // Handle direct array format
          transformedData = data.map((item, index) => {
            const batchDate = dayjs(item.BatchDate);
            const expiryDate = batchDate.add(item.BestForDays, 'day');
            const today = dayjs();
            const daysToExpire = expiryDate.diff(today, 'day');
            
            return {
              id: index + 1,
              sno: index + 1,
              batchNumber: item.BatchNumber,
              prodPlanID: item.ProdPlanID,
              productionPlantID: item.BranchID,
              productionPlantName: item.OriginBranchName || item.StoreName || 'N/A',
              salesBranchID: item.BranchID,
              salesBranchName: item.StoreName || 'N/A',
              itemId: item.ItemId,
              itemName: item.ItemName,
              batchDate: batchDate.format('DD-MM-YYYY'),
              todaysDate: today.format('DD-MM-YYYY'),
              bestForDays: item.BestForDays,
              prodQty: item.ProdQty || 0,
              totalSaleQty: item.SaleQty || 0,
              totalSRQty: item.SRQty || 0,
              pshopQty: item.PshopQty || 0,
              saleDetails: [],
              expiryDate: expiryDate.format('DD-MM-YYYY'),
              daysToExpire: daysToExpire,
              expiringQty: (item.ProdQty || 0) - (item.SaleQty || 0) + (item.SRQty || 0) - (item.PshopQty || 0),
              branchID: item.BranchID,
              branchName: item.StoreName || 'N/A',
              storeName: item.StoreName,
              prodPlantName: item.ProdPlantName,
              areaName: item.AreaName,
              age: item.Age,
              daysToExpiry: daysToExpire,
              remainingQty: (item.ProdQty || 0) - (item.SaleQty || 0) + (item.SRQty || 0) - (item.PshopQty || 0)
            };
          });
        }

        setRawData(transformedData);
        setFilteredData(transformedData);

        // Show appropriate message based on data availability
        if (transformedData.length === 0) {
          console.log('No data found for the selected criteria');
          // Don't show alert for no data, let the UI handle it
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch near expiry data:', response.status, errorText);
        alert(`Failed to fetch data: ${response.status} - ${errorText}`);
        setRawData([]);
        setFilteredData([]);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching near expiry data:', error);
        alert('Error fetching data. Please check your connection and try again.');
      }
      setRawData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data
  useEffect(() => {
    setRawData([]);
    setFilteredData([]);
    if (authToken && userId) {
      fetchItems();
    }
  }, [authToken, userId]);

  // Apply filters (now calls API)
  const applyFilters = () => {
    fetchNearExpiryData();
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setSelectedItemObj(null);
    setSelectedBatch('');
    setBranchType('');
    setFromDate(dayjs().subtract(1, 'month'));
    setToDate(dayjs());
    setLifeRangeFrom(1);
    setLifeRangeTo(10);
    setSearchTerm('');
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setRawData([]);
    setFilteredData([]);
  };

const exportToXLSX = () => {
  if (filteredAndSearchedData.length === 0) {
    alert('No data to export.');
    return;
  }

  const exportData = [];

  filteredAndSearchedData.forEach(row => {
    const daysToExpireDisplay = row.daysToExpire < 0 ? 'N/A' : row.daysToExpire;

    if (row.saleDetails && row.saleDetails.length > 0) {
      row.saleDetails.forEach(detail => {
        const detailRow = {
          'Batch Number': row.batchNumber,
          'Batch Date': row.batchDate,
          'Expiry Date': row.expiryDate,
          'Today\'s Date': row.todaysDate,
          'Days to Expire': daysToExpireDisplay,
          'Production Plant ID': row.productionPlantID,
          'Production Plant Name': row.productionPlantName,
          'Item ID': row.itemId,
          'Item Name': row.itemName,
          'Best For Days': row.bestForDays,
          'Prod Qty': row.prodQty,
          'Total Sale Qty': row.totalSaleQty,
          'Total SR Qty': row.totalSRQty,
          'Pack to Loose': row.pshopQty || 0,
          'Expiring Quantity': row.expiringQty,
          'Sale Branch ID': detail.BranchId,
          'Sale Branch Name': detail.BranchName,
          'Transfer In Qty': detail.TrInQty || 0,
          'Sale Qty': detail.SaleQty || 0,
          'SR Qty': detail.SRQty || 0
        };
        exportData.push(detailRow);
      });
    } else {
      const mainRow = {
        'Batch Number': row.batchNumber,
        'Batch Date': row.batchDate,
        'Expiry Date': row.expiryDate,
        'Today\'s Date': row.todaysDate,
        'Days to Expire': daysToExpireDisplay,
        'Production Plant ID': row.productionPlantID,
        'Production Plant Name': row.productionPlantName,
        'Item ID': row.itemId,
        'Item Name': row.itemName,
        'Best For Days': row.bestForDays,
        'Prod Qty': row.prodQty,
        'Total Sale Qty': row.totalSaleQty,
        'Total SR Qty': row.totalSRQty,
        'Pack to Loose': row.pshopQty || 0,
        'Expiring Quantity': row.expiringQty,
        'Sale Branch ID': '',
        'Sale Branch Name': '',
        'Transfer In Qty': '',
        'Sale Qty': '',
        'SR Qty': ''
      };
      exportData.push(mainRow);
    }
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Near Expiry Report');
  XLSX.writeFile(wb, 'near_expiry_detailed_report.xlsx');
};



  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Search and sort data with better search functionality
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
        (item.itemId && item.itemId.toString().toLowerCase().includes(lower)) ||
        (item.batchNumber && item.batchNumber.toString().toLowerCase().includes(lower)) ||
        (item.storeName && item.storeName.toString().toLowerCase().includes(lower)) ||
        (item.areaName && item.areaName.toString().toLowerCase().includes(lower))
      );
    }

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: index + 1
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle date sorting for specific date fields
      if (sortConfig.key === 'batchDate' || sortConfig.key === 'expiryDate' || sortConfig.key === 'todaysDate') {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const [day, month, year] = dateStr.split('-');
          return new Date(year, month - 1, day);
        };
        
        const aDate = parseDate(aValue);
        const bDate = parseDate(bValue);
        
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: index + 1
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Function to get expiry status styling
  const getExpiryStatus = (daysToExpiry) => {
    if (daysToExpiry < 0) return { class: 'expired', text: 'Expired' };
    if (daysToExpiry <= 2) return { class: 'critical', text: 'Critical' };
    if (daysToExpiry <= 7) return { class: 'warning', text: 'Warning' };
    return { class: 'good', text: 'Good' };
  };

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header />
      
      <div className={styles.indent_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* GT/MT Information Banner */}
        <GTMTInfoBanner />

        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* UPDATED: Area Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas}
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* UPDATED: Branch Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => {
                  setSelectedBranchObj(newValue);
                }}
                disabled={!selectedAreaObj || loadingBranches}
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className={styles.indent_filter_actions}>
            <button 
              onClick={applyFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button 
              onClick={clearFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
            >
              Clear
            </button>
            <button 
              onClick={exportToXLSX} 
              className={`${styles.indent_btn} ${styles.indent_btn_success}`}
            >
              Export XLSX
            </button>
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Item Name, Item ID, Batch Number, Store Name, or Area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.indent_results_summary}>
          <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data} style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              margin: '20px 0'
            }}>
              <div style={{
                fontSize: '48px',
                color: '#6c757d',
                marginBottom: '16px'
              }}>
                
              </div>
              <h3 style={{
                color: '#495057',
                marginBottom: '12px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                No Data Found
              </h3>
              <p style={{
                color: '#6c757d',
                marginBottom: '8px',
                fontSize: '14px'
              }}>
                No near expiry data found for the selected criteria.
              </p>
              <p style={{
                color: '#6c757d',
                fontSize: '14px'
              }}>
                Please select a Segment and Area, then click Apply to fetch data.
              </p>
              {/* Show additional info for GT/MT segments */}
              {(branchType === 'GT' || branchType === 'MT') && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px 20px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '8px',
                  border: '2px solid #2196f3',
                  fontSize: '14px',
                  color: '#1565c0'
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px' }}>📍</span>
                    <strong>{branchType} Segment - Available Production Data:</strong>
                  </div>
                  <div style={{ marginLeft: '24px' }}>
                    <div>• <strong>Plant L111</strong> production data available</div>
                    <div>• <strong>Plant L135</strong> production data available</div>
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: '#1976d2'
                    }}>
                      Select the appropriate area and branch to view near expiry data from these plants.
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              {/* Calculation Formula: Expiring Qty = Prod Qty - Total Sale Qty + Total SR Qty - Pack to Loose */}
              {/* <div style={{ 
                backgroundColor: '#e3f2fd', 
                padding: '8px 12px', 
                marginBottom: '10px', 
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1976d2',
                border: '1px solid #bbdefb'
              }}>
                 Formula: <span style={{ fontFamily: 'monospace', backgroundColor: '#fff', padding: '2px 6px', borderRadius: '3px' }}>
                  Expiring Qty = Prod Qty - Total Sale Qty + Total SR Qty - Pack to Loose
                </span>
              </div> */}
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th className={styles.indent_non_sortable}>S.No</th>
                    <th onClick={() => handleSort('batchNumber')} className={styles.indent_sortable}>
                      Batch Number {sortConfig.key === 'batchNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('productionPlantID')} className={styles.indent_sortable}>
                      Production Plant ID {sortConfig.key === 'productionPlantID' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('productionPlantName')} className={styles.indent_sortable}>
                      Production Plant Name {sortConfig.key === 'productionPlantName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemId')} className={styles.indent_sortable}>
                      Item ID {sortConfig.key === 'itemId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemName')} className={styles.indent_sortable}>
                      Item Name {sortConfig.key === 'itemName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('batchDate')} className={styles.indent_sortable}>
                      Batch Date {sortConfig.key === 'batchDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('expiryDate')} className={styles.indent_sortable}>
                      Expiry Date {sortConfig.key === 'expiryDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('todaysDate')} className={styles.indent_sortable}>
                      Today's Date {sortConfig.key === 'todaysDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('bestForDays')} className={styles.indent_sortable}>
                      Best For Days {sortConfig.key === 'bestForDays' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('daysToExpire')} className={styles.indent_sortable}>
                      Days to Expire {sortConfig.key === 'daysToExpire' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('prodQty')} className={styles.indent_sortable}>
                      Prod Qty {sortConfig.key === 'prodQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('totalSaleQty')} className={styles.indent_sortable}>
                      Total Sale Qty {sortConfig.key === 'totalSaleQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('totalSRQty')} className={styles.indent_sortable}>
                      Total SR Qty {sortConfig.key === 'totalSRQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('pshopQty')} className={styles.indent_sortable} 
                        title="Pack to Loose Quantity - Items converted from pack to loose form"
                        style={{ backgroundColor: '#fff8e1', position: 'relative' }}>
                      Pack to Loose {sortConfig.key === 'pshopQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      <span style={{ fontSize: '11px', color: '#f57c00', marginLeft: '4px' }}>ⓘ</span>
                    </th>
                    <th onClick={() => handleSort('expiringQty')} className={styles.indent_sortable}>
                      Expiring Quantity {sortConfig.key === 'expiringQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => {
                    return (
                      <tr key={row.id}>
                        <td>{row.sno}</td>
                        <td className={styles.indent_ticket_id}>{row.batchNumber}</td>
                        <td className={styles.indent_ticket_id}>{row.productionPlantID}</td>
                        <td>{row.productionPlantName}</td>
                        <td className={styles.indent_ticket_id}>{row.itemId}</td>
                        <td>{row.itemName}</td>
                        <td>{row.batchDate}</td>
                        <td>{row.expiryDate}</td>
                        <td>{row.todaysDate}</td>
                        <td className={styles.indent_amount}>{row.bestForDays}</td>
                        <td className={`${styles.indent_amount} ${row.daysToExpire < 0 ? styles.indent_status_expired : row.daysToExpire <= 2 ? styles.indent_status_critical : row.daysToExpire <= 7 ? styles.indent_status_warning : styles.indent_status_good}`}>
                          {row.daysToExpire < 0 ? 'N/A' : `${row.daysToExpire} days`}
                        </td>
                        <td className={styles.indent_amount}>{row.prodQty}</td>
                        <td className={styles.indent_amount}>{row.totalSaleQty}</td>
                        <td className={styles.indent_amount}>{row.totalSRQty}</td>
                        <td className={styles.indent_amount} style={{ backgroundColor: '#fff8e1', fontWeight: '600' }}>{row.pshopQty || 0}</td>
                        <td className={styles.indent_amount}>{row.expiringQty}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.indent_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default NearExpiry;